import React, { useContext, useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { tryClaimAutoOpen } from "@components/tickets/ticketPurchaseDialogCoordinator"
import { EventDateFormat, EventTimeFormat } from "@util/dates"
import { getTicketPriceDisplay } from "@util/ticketUtils"
import FreeTextFormat from "@components/shared/FreeTextFormat"
import CommentSection from "@components/comments/CommentSection"
import { isPast } from "date-fns"
import { UserContext } from "@components/HomeApp"
import { TextAsImage } from "./TextAsImage"
import { PostListItemExpandedStyles } from "./constants"
import InterestedBlock from "./InterestedBlock"
import EventForm from "@components/EventForm"
import { ShareUrlButton } from "@components/ShareUrlButton"
import ConcertLineup from "@components/shared/ConcertLineup"
import ConcertMediaScreen from "@components/comments/ConcertMediaScreen"
import GoogleMap from "@components/shared/GoogleMap"
import Marker from "@components/shared/Marker"
import { ShowHideButton } from "@components/shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import GoingBlock from "./GoingBlock"
import RelatedPosts from "@components/RelatedPosts"
import TicketPurchaseComponent from "@components/tickets/TicketPurchaseComponent"
import TicketPurchaseComponentLoggedOut from "@components/tickets/TicketPurchaseComponentLoggedOut"
import PostDonationComponent from "@components/tips/PostDonationComponent"
import PostDonationComponentLoggedOut from "@components/tips/PostDonationComponentLoggedOut"
import BandNadaTicketRSVPText from "@components/tickets/BandNadaTicketRSVPText"
import clsx from "clsx"
import PremiereHeader from "./PremiereHeader"
import styles from "./styles.module.css"
import EditManageHeader from "@components/EditManageHeader"
import CollaborationInviteHeader from "@components/CollaborationInviteHeader"
import { Concert, CollaboratorEntry } from "@models/events"

interface ConcertListItemExpandedProps {
  concert: Concert
  setPostListItem: (concert: Concert) => void
  updateStatus?: () => void
  adminMode?: boolean
  isPostPage?: boolean
}

const ConcertListItemExpanded: React.FC<ConcertListItemExpandedProps> = ({
  concert,
  setPostListItem,
  updateStatus = () => {},
  adminMode = false,
  isPostPage = false,
}) => {
  const metaClass = isPostPage
    ? "col-span-12 pt-2"
    : PostListItemExpandedStyles.metaDiv
  const commentsClass = isPostPage
    ? "col-span-12 pt-4"
    : PostListItemExpandedStyles.commentsDiv
  const [postStatus, setPostStatus] = useState(concert.status)
  const [collaborators, setCollaborators] = useState<CollaboratorEntry[]>(
    concert.post_collaborators || [],
  )
  const { googlek, user } = useContext(UserContext)
  const location = useLocation()
  const isOwner =
    user &&
    (user.band_id === concert.band_id ||
      collaborators.some(
        (c) => c.band_id === user.band_id && c.status === "accepted",
      ))
  const pendingCollaborator =
    user &&
    collaborators.find(
      (c) => c.band_id === user.band_id && c.status === "pending",
    )
  const acceptedCollaborators = collaborators.filter(
    (c) => c.status === "accepted",
  )
  const isPastEvent = concert.end_dt ? isPast(new Date(concert.end_dt)) : false
  const loadEdit =
    new URLSearchParams(window.location.search).get("edit") === "true"
  const [showEditForm, setShowEditForm] = useState(!!(isOwner && loadEdit))

  // Check if URL parameter indicates we should auto-open ticket purchase dialog
  const urlSaysAutoOpen =
    new URLSearchParams(location.search).get("auto-open-ticket-purchase") ===
    "true"

  // Use state to track if this component should auto-open
  const [shouldAutoOpenTicketPurchase, setShouldAutoOpenTicketPurchase] =
    useState(false)

  // Use useEffect to atomically check and claim the flag
  useEffect(() => {
    if (urlSaysAutoOpen) {
      const claimed = tryClaimAutoOpen()
      if (claimed) {
        setShouldAutoOpenTicketPurchase(true)
      } else {
        setShouldAutoOpenTicketPurchase(false)
      }
    }
  }, [urlSaysAutoOpen, concert.id])

  const setConcert = (newConcert: Concert) => {
    setPostListItem(newConcert)
  }

  const imgStyle = {
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: "black",
  }

  return showEditForm ? (
    <EventForm
      setShowForm={setShowEditForm}
      concert={concert}
      setConcert={setConcert}
    />
  ) : concert.status === "trashed" ? (
    <div className="grid grid-cols-12">
      <div className="col-span-12 font-bold">
        Sorry, this event has been removed. You can check the{" "}
        <Link to={concert.local_path ?? ""} className="text-blue-600">
          band's page
        </Link>{" "}
        to see if they replaced it with another event.
      </div>
    </div>
  ) : (
    <div className="grid grid-cols-12">
      <PremiereHeader post={concert} size="large" />
      {concert.cover_image_url_large ? (
        <div className={PostListItemExpandedStyles.contentDiv}>
          <ConcertMediaScreen
            postId={concert.id}
            coverImageUrl={concert.cover_image_url_large}
            attachedMedia={
              concert.cover_image_cropped ? concert.attached_media || [] : []
            }
            showHeader={false}
            coverMode={true}
            className=""
          />
        </div>
      ) : (
        <div className={PostListItemExpandedStyles.contentDiv}>
          <div className="w-full">
            <TextAsImage
              text={
                concert.status === "canceled" || concert.canceled_at
                  ? `Canceled! ${concert.title}`
                  : concert.status === "unpublished"
                    ? `${concert.title} (unlisted)`
                    : concert.title
              }
            />
          </div>
        </div>
      )}
      <div className={PostListItemExpandedStyles.metaDiv}>
        {pendingCollaborator ? (
          <CollaborationInviteHeader
            postId={concert.id}
            collaborator={pendingCollaborator}
            onAccepted={() =>
              setCollaborators((prev) =>
                prev.map((c) =>
                  c.band_id === user.band_id ? { ...c, status: "accepted" } : c,
                ),
              )
            }
            onDeclined={() =>
              setCollaborators((prev) =>
                prev.map((c) =>
                  c.band_id === user.band_id ? { ...c, status: "declined" } : c,
                ),
              )
            }
          />
        ) : (
          <EditManageHeader
            isOwner={isOwner}
            post={concert as Concert & { id: number }}
            setShowEditForm={setShowEditForm}
            band={user?.band}
          />
        )}
        <div
          style={{ position: "sticky", top: 3, zIndex: 10 }}
          className="bg-white pb-2"
        >
          <div className="flex items-start justify-between">
            <div
              className={
                concert.status === "canceled" || concert.canceled_at
                  ? "text-red-700"
                  : ""
              }
            >
              <h2
                className={clsx(
                  PostListItemExpandedStyles.header,
                  "inline mr-2",
                  concert.is_bn_premiere && styles.premiereTitle,
                )}
              >
                {concert.status === "canceled" || concert.canceled_at ? (
                  <>
                    Canceled!{" "}
                    <span
                      className="line-through"
                      dangerouslySetInnerHTML={{ __html: concert.title ?? "" }}
                    />
                  </>
                ) : (
                  <>
                    <span
                      dangerouslySetInnerHTML={{ __html: concert.title ?? "" }}
                    />
                    {concert.status === "unpublished" && (
                      <span className="text-xs font-normal text-gray-500">
                        {" "}
                        (unlisted)
                      </span>
                    )}
                  </>
                )}
              </h2>
              {(concert.status === "canceled" || concert.canceled_at) &&
              concert.canceled_event_message ? (
                <>
                  <h6
                    className={clsx(
                      PostListItemExpandedStyles.secondaryDescriptor,
                      "mt-3",
                    )}
                  >
                    Cancelation Message from {concert.band_name}
                  </h6>
                  <p className="mb-3 mt-1 text-red-700 text-sm">
                    {concert.canceled_event_message}
                  </p>
                </>
              ) : null}
            </div>
            <ShareUrlButton nadaUrl={concert.nada_url} renderMode="button" />
          </div>

          <h6 className={PostListItemExpandedStyles.secondaryHeader}>
            {concert.status === "needs_approved" && isOwner ? (
              <span className="text-red-600 ml-4">
                Hidden Event{" "}
                <button
                  className="text-blue-700 ml-2 border-b-2 border-blue-300 focus-outline-none"
                  onClick={() => setShowEditForm(true)}
                >
                  Publish for your fans now
                </button>
              </span>
            ) : null}
          </h6>

          {isOwner &&
            concert.announced_at &&
            new Date(concert.announced_at) > new Date() && (
              <p className="text-sm text-blue-700 mb-2">
                Announcing on{" "}
                {new Date(concert.announced_at).toLocaleString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}{" "}
                — hidden from fans until then.
              </p>
            )}

          {isPastEvent && !(concert.status === "canceled" || concert.canceled_at) && (
            <div className="border-b border-red-700 border-t font-bold italic text-red-700 text-xs mb-6">
              Past event
            </div>
          )}

          {concert.status === "canceled" || concert.canceled_at ? (
            concert.ticket_type === "bNTickets" &&
            concert.user_ticket_purchase_status === "cancelled_and_refunded" ? (
              <p className="text-sm text-gray-600">You have been refunded.</p>
            ) : null
          ) : concert.ticket_type === "bNTickets" ? (
            user ? (
              (!isPastEvent || (concert.user_ticket_count ?? 0) > 0) && (
                <TicketPurchaseComponent
                  bandId={concert.band_id}
                  bandName={concert.band_name}
                  userName={user.name}
                  userDefaultPaymentMethodLastFour={
                    user.default_payment_method_last_four
                  }
                  userDefaultPaymentMethodExpired={
                    user.default_payment_method_expired
                  }
                  postId={concert.id}
                  ticketPrice={concert.ticket_price}
                  maxTickets={concert.band_nada_ticket_max ?? 0}
                  maxTicketsPerUser={
                    concert.band_nada_max_tickets_per_user ?? undefined
                  }
                  ticketsSold={concert.band_nada_tickets_sold ?? 0}
                  eventEndDate={concert.end_dt}
                  autoOpenDialog={shouldAutoOpenTicketPurchase}
                  ticketPriceAtDoor={concert.ticket_price_at_door}
                  concert={concert}
                />
              )
            ) : !isPastEvent ? (
              <TicketPurchaseComponentLoggedOut
                postId={concert.id}
                bandId={concert.band_id}
                bandName={concert.band_name}
                eventTitle={concert.title}
                ticketPrice={concert.ticket_price}
                maxTickets={concert.band_nada_ticket_max ?? 0}
                ticketsSold={concert.band_nada_tickets_sold ?? 0}
                eventEndDate={concert.end_dt}
                ticketPriceAtDoor={concert.ticket_price_at_door}
                concert={concert as unknown as { [key: string]: unknown }}
              />
            ) : null
          ) : !isPastEvent ? (
            <InterestedBlock
              postId={concert.id}
              renderMode="full"
              maxRsvps={concert.max_rsvps}
            />
          ) : null}

          {(() => {
            const price = concert.ticket_type === "bNTickets" ? null : getTicketPriceDisplay(concert as unknown as { [key: string]: unknown })
            if (!price && !concert.ticket_link_url) return null
            return (
              <div className="font-bold text-sm">
                {price}
                {price && concert.ticket_link_url && " - "}
                {concert.ticket_link_url && (
                  <a
                    href={concert.ticket_link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600"
                  >
                    Ticket Link
                  </a>
                )}
              </div>
            )
          })()}
          <div
            className={clsx(
              PostListItemExpandedStyles.postDate,
              (concert.status === "canceled" || concert.canceled_at) &&
                "line-through",
            )}
          >
            {EventDateFormat(concert.start_dt, concert.timezone)}~
            {EventTimeFormat(concert.end_dt, concert.timezone)}
            &nbsp;
            <a
              href={concert.add_to_calendar_url ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700"
            >
              (+ add to calendar)
            </a>
          </div>
          {concert.post_type === "concert" && (
            <div className={PostListItemExpandedStyles.externalLink}>
              <a href={concert.external_url ?? undefined} target="_blank" rel="noreferrer">
                view on&nbsp;
                {concert.class_name}
              </a>
            </div>
          )}

          {concert.event_type === "bNliveStream" ? (
            <div className="text-sm">
              <a href={concert.nada_url} className="text-gray-700">
                {concert.nada_url}
              </a>
            </div>
          ) : concert.bn_venue ? (
            <div className="text-sm">
              <Link
                to={concert.bn_venue.bandnada_path ?? ""}
                className="text-green-700 font-bold leading-none"
              >
                {concert.bn_venue.name}
              </Link>
            </div>
          ) : concert.venue_name || concert.venue_city_display_name ? (
            <div className="text-sm">
              <a
                className="text-gray-700"
                href={concert.google_map_url ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                {concert.venue_name && concert.venue_city_display_name
                  ? `${concert.venue_name} in ${concert.venue_city_display_name}`
                  : `${concert.venue_name}${concert.venue_city_display_name}`}
              </a>
            </div>
          ) : null}
          <div className="leading-tight">
            <div className={PostListItemExpandedStyles.postedBy}>
              Posted by{" "}
              <Link to={concert.local_path ?? ""}>{concert.band_name}</Link>
              {acceptedCollaborators.length > 0 && (
                <>
                  {" w/ "}
                  {acceptedCollaborators.map((collaborator, index) => (
                    <React.Fragment
                      key={collaborator.id ?? collaborator.band_id}
                    >
                      {index > 0 && ", "}
                      {collaborator.band_path ? (
                        <Link to={collaborator.band_path}>
                          {collaborator.band_name}
                        </Link>
                      ) : (
                        collaborator.band_name
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>
            {concert.pricing_type === "free" &&
              concert.accepts_donation &&
              !isPastEvent && (
                <div className="mt-4 mb-2">
                  {user ? (
                    <PostDonationComponent
                      postId={concert.id}
                      bandId={concert.band_id}
                      postTitle={concert.title}
                      userName={user.name}
                      userEmail={user.email}
                      userDefaultPaymentMethodLastFour={
                        user.default_payment_method_last_four
                      }
                      donationText={concert.donation_text}
                    />
                  ) : (
                    <PostDonationComponentLoggedOut
                      postId={concert.id}
                      bandId={concert.band_id}
                      postTitle={concert.title}
                      donationText={concert.donation_text}
                    />
                  )}
                </div>
              )}
          </div>
        </div>


        <GoingBlock postId={concert.id} isPastEvent={isPastEvent} />

        <div className="mt-4">
          <ReactionBlock
            contentType="Post"
            contentId={concert.id}
            postId={concert.id}
          />
        </div>

        <ConcertLineup
          postId={concert.id}
          startTime={concert.start_dt as string | null}
          timezone={concert.timezone}
        />

        {concert.description && (
          <>
            <h6
              className={clsx(
                PostListItemExpandedStyles.secondaryDescriptor,
                "mt-4",
              )}
            >
              Event Details
            </h6>
            <div className={PostListItemExpandedStyles.description}>
              <FreeTextFormat
                text={concert.description}
                expandable={false}
                expanded={true}
                linkify={true}
              />
            </div>
          </>
        )}

        {/* RSVP text for bandNada ticket events */}
        {concert.ticket_type === "bNTickets" &&
          !(concert.status === "canceled" || concert.canceled_at) &&
          user &&
          !isOwner &&
          !concert.user_has_purchased_ticket && (
            <BandNadaTicketRSVPText
              postId={concert.id}
              eventEndDate={concert.end_dt}
            />
          )}

        <RelatedPosts postId={concert.id} className="mt-8 mb-4" nadaUrl={concert.nada_url} />
      </div>
      <div className={commentsClass}>
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}

        <CommentSection
          postId={concert.id}
          headerText="The Peanut Gallery Says:"
          className="mb-4"
        />
      </div>
    </div>
  )
}

export default ConcertListItemExpanded
